'use client';

import type React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Card, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { AIChat } from '@/types/chat';

interface ChatListItemProps {
  chat: AIChat;
  onDeleteClick: (chat: AIChat) => void;
  onShiftDelete?: (chat: AIChat) => void; // Add this new prop
  isDeleting: boolean;
}

const ChatListItem: React.FC<ChatListItemProps> = ({ chat, onDeleteClick, onShiftDelete, isDeleting }) => {
  const formattedDate = chat.updated
    ? formatDistanceToNow(new Date(chat.updated * 1000), { addSuffix: true })
    : 'Unknown date';

  const handleDeleteClick = (e: React.MouseEvent) => {
    if (e.shiftKey && onShiftDelete) {
      onShiftDelete(chat);
    } else {
      onDeleteClick(chat);
    }
  };

  return (
    <Card className="flex flex-col h-full transition-all duration-200 hover:shadow-md">
      <CardHeader className="pb-4">
        <CardTitle className="truncate text-lg">{chat.summary || 'Untitled Chat'}</CardTitle>
        <p className="text-xs text-muted-foreground">{formattedDate}</p>
      </CardHeader>
      <CardFooter className="pt-2 flex justify-between gap-2">
        <Button variant="outline" size="sm" asChild className="flex-1">
          <Link to={`/ai-chat?id=${chat.id}`}>Open Chat</Link>
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDeleteClick} // Update to use our new handler
          disabled={isDeleting}
          className="px-2"
          aria-label="Delete chat"
          title="Delete chat (Shift+Click to skip confirmation)" // Add a helpful tooltip
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ChatListItem;
